import type {
  CodRiskScoreThresholds,
  CodRiskSettingsDTO,
} from "@/modules/customers/domain/cod-risk-settings";
import { scoreCustomerRisk } from "@/modules/customers/domain/service/score-customer-risk";
import type {
  CustomerRiskDTO,
  CustomerRiskFeaturesDTO,
  CustomerRiskTier,
} from "@/modules/customers/domain/types";

export const COD_OVERRIDE_NOTE_PREFIX = "[COD override]";

/**
 * Merge COD override reason into order note.
 * - reason set → replace any prior `[COD override]` lines
 * - reason empty + clear=true → strip override lines (e.g. switched to prepaid)
 * - reason empty + clear=false → leave note unchanged (edit flow without re-confirm)
 */
export function appendCodOverrideNote(
  note: string,
  reason: string | null | undefined,
  options?: { clear?: boolean },
): string | null {
  const withoutOverride = note
    .split("\n")
    .filter((line) => !line.trim().startsWith(COD_OVERRIDE_NOTE_PREFIX))
    .join("\n")
    .trim();
  const trimmedReason = reason?.trim();

  if (options?.clear) return withoutOverride || null;
  if (!trimmedReason) {
    const clean = note.trim();
    return clean || null;
  }

  const overrideLine = `${COD_OVERRIDE_NOTE_PREFIX} ${trimmedReason}`;
  return withoutOverride ? `${withoutOverride}\n${overrideLine}` : overrideLine;
}

export function needsCodSoftConfirm(
  tier: CustomerRiskTier | undefined,
): boolean {
  return tier === "require_deposit" || tier === "prepaid_only";
}

export function toScoreThresholds(
  settings: CodRiskSettingsDTO | undefined | null,
): Partial<CodRiskScoreThresholds> | undefined {
  if (!settings) return undefined;
  return {
    defaultDepositPct: settings.depositPct,
    newCustomerTotalLak: settings.newCustomerTotalThreshold,
    depositScoreMin: settings.depositScoreMin,
    prepaidScoreMin: settings.prepaidScoreMin,
    staleShippedCodDays: settings.staleShippedCodDays,
  };
}

type BadgeVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "success";

export const CUSTOMER_RISK_TIER_META: Record<
  CustomerRiskTier,
  {
    label: string;
    shortLabel: string;
    actionLabel: string;
    variant: BadgeVariant;
    bannerClass: string;
  }
> = {
  allow_cod: {
    label: "COD \u0ec4\u0e94\u0ec9",
    shortLabel: "COD \u0ec4\u0e94\u0ec9",
    actionLabel: "\u0ec1\u0e99\u0eb0\u0e99\u0eb3: COD \u0ec0\u0e95\u0eb1\u0ea1\u0ec4\u0e94\u0ec9",
    variant: "success",
    bannerClass: "border-leaf/30 bg-leaf/10 text-foreground",
  },
  require_deposit: {
    label: "\u0ec1\u0e99\u0eb0\u0e99\u0eb3\u0ea1\u0eb1\u0e94\u0e88\u0eb3",
    shortLabel: "\u0ea1\u0eb1\u0e94\u0e88\u0eb3",
    actionLabel:
      "\u0ec1\u0e99\u0eb0\u0e99\u0eb3: \u0ec2\u0ead\u0e99\u0ea1\u0eb1\u0e94\u0e88\u0eb3\u0e81\u0ec8\u0ead\u0e99",
    variant: "secondary",
    bannerClass: "border-secondary/40 bg-secondary/10 text-foreground",
  },
  prepaid_only: {
    label: "\u0ec1\u0e99\u0eb0\u0e99\u0eb3\u0ec2\u0ead\u0e99\u0e81\u0ec8\u0ead\u0e99",
    shortLabel: "\u0ec2\u0ead\u0e99\u0e81\u0ec8\u0ead\u0e99",
    actionLabel:
      "\u0ec1\u0e99\u0eb0\u0e99\u0eb3: \u0ec2\u0ead\u0e99\u0e81\u0ec8\u0ead\u0e99\u0ec0\u0e95\u0eb1\u0ea1",
    variant: "destructive",
    bannerClass: "border-destructive/30 bg-destructive/10 text-foreground",
  },
};

export function customerRiskTierMeta(tier: CustomerRiskTier) {
  return CUSTOMER_RISK_TIER_META[tier];
}

export function formatDepositHint(risk: CustomerRiskDTO): string | null {
  if (risk.tier === "allow_cod") return null;
  if (risk.suggestedDepositPct >= 100) {
    return "\u0ec1\u0e99\u0eb0\u0e99\u0eb3: \u0ec2\u0ead\u0e99\u0e81\u0ec8\u0ead\u0e99 100%";
  }
  return `\u0ec1\u0e99\u0eb0\u0e99\u0eb3: \u0ec2\u0ead\u0e99\u0ea1\u0eb1\u0e94\u0e88\u0eb3 ~${risk.suggestedDepositPct}%`;
}

/** Suggested deposit amount in LAK from proposed total + deposit %. */
export function suggestedDepositAmount(risk: CustomerRiskDTO): number | null {
  if (risk.tier === "allow_cod") return null;
  const total =
    risk.proposedTotal != null ? Number(risk.proposedTotal) : Number.NaN;
  if (!Number.isFinite(total) || total <= 0) return null;
  const pct = Math.max(0, Math.min(100, risk.suggestedDepositPct));
  return Math.round((total * pct) / 100);
}

export function recommendedPaymentType(
  tier: CustomerRiskTier | undefined,
): "prepaid" | "cod" {
  if (tier === "allow_cod") return "cod";
  return "prepaid";
}

function emptyFeatures(): CustomerRiskFeaturesDTO {
  return {
    orderCount: 0,
    confirmedOrderCount: 0,
    totalRevenue: "0",
    isReturning: false,
    codOrderCount: 0,
    codPickedUpCount: 0,
    codReturnedCount: 0,
    codSuccessRate: null,
    returnCount: 0,
    returnRate: null,
    shippedLikeCount: 0,
    avgOrderValue: null,
    maxOrderValue: null,
    avgPickupDays: null,
    staleCodCount: 0,
    lastReturnedAt: null,
    lastCodPickedUpAt: null,
    lastShippingCarrier: null,
    lastShippingBranch: null,
    branchReturnRate: null,
    branchShippedCount: 0,
    branchReturnedCount: 0,
  };
}

/** Lightweight risk when customer is not saved yet (new walk-in). */
export function scoreDraftCustomerRisk(
  proposedTotal: number | null | undefined,
  thresholds?: Partial<CodRiskScoreThresholds>,
): CustomerRiskDTO {
  return scoreCustomerRisk("draft", emptyFeatures(), {
    proposedTotal:
      proposedTotal != null && Number.isFinite(proposedTotal)
        ? proposedTotal
        : null,
    thresholds,
  });
}
